import { useEffect, useRef, useState } from "react";
import { Zap } from "lucide-react";

import {
  formatQuickTopupCountdown,
  quickTopupClockSkewMs,
  type QuickTopupOffer,
} from "@/lib/quick-topup";

const DRAG_THRESHOLD_PX = 5;

/**
 * Pill nổi hiển thị đồng hồ đếm ngược Quick Topup khi user đã đóng popup —
 * để user luôn biết ưu đãi vẫn đang chạy. Bấm vào mở lại popup nạp nhanh.
 * Kéo thả được (giống bong bóng tour guide). Tự ẩn khi: không có offer,
 * đã tiêu thụ, hoặc hết hạn. Neo giờ server (skew) cho countdown.
 */
export function QuickTopupCountdownPill({
  offer,
  hidden,
  onClick,
}: {
  offer?: QuickTopupOffer;
  hidden?: boolean;
  onClick: () => void;
}) {
  const [now, setNow] = useState(() => Date.now());

  // Kéo thả — mirror bong bóng tour guide trong top-nav.tsx
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [dragPos, setDragPos] = useState({ x: 0, y: 0 });
  const dragStart = useRef<{ x: number; y: number; posX: number; posY: number } | null>(null);
  const hasDragged = useRef(false);

  const active = Boolean(offer && !offer.consumed && !hidden);
  useEffect(() => {
    if (!active) return;
    const timer = window.setInterval(() => setNow(Date.now()), 1_000);
    return () => window.clearInterval(timer);
  }, [active]);

  const handlePointerDown = (e: React.PointerEvent<HTMLButtonElement>) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    dragStart.current = { x: e.clientX, y: e.clientY, posX: dragPos.x, posY: dragPos.y };
    hasDragged.current = false;
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLButtonElement>) => {
    if (!dragStart.current) return;
    const dx = e.clientX - dragStart.current.x;
    const dy = e.clientY - dragStart.current.y;
    if (Math.abs(dx) > DRAG_THRESHOLD_PX || Math.abs(dy) > DRAG_THRESHOLD_PX) {
      hasDragged.current = true;
    }
    if (buttonRef.current) {
      buttonRef.current.style.transform = `translate3d(${dragStart.current.posX + dx}px, ${dragStart.current.posY + dy}px, 0)`;
    }
  };

  const handlePointerUp = (e: React.PointerEvent<HTMLButtonElement>) => {
    if (!dragStart.current) return;
    e.currentTarget.releasePointerCapture(e.pointerId);
    const dx = e.clientX - dragStart.current.x;
    const dy = e.clientY - dragStart.current.y;
    setDragPos({ x: dragStart.current.posX + dx, y: dragStart.current.posY + dy });
    dragStart.current = null;
  };

  const handleClick = (e: React.MouseEvent) => {
    // Vừa kéo thì bỏ qua click (không mở popup) — giống tour bubble
    if (hasDragged.current) {
      e.preventDefault();
      e.stopPropagation();
      hasDragged.current = false;
      return;
    }
    onClick();
  };

  if (!offer || offer.consumed || hidden) return null;

  const serverNow = now + quickTopupClockSkewMs(offer);
  const remainingSeconds = Math.max(
    0,
    Math.ceil((new Date(offer.expires_at).getTime() - serverNow) / 1_000),
  );
  if (remainingSeconds <= 0) return null;

  const percent = Math.round(offer.bonus_rate * 100);
  const { text: countdown, unit } = formatQuickTopupCountdown(remainingSeconds);

  return (
    <button
      ref={buttonRef}
      type="button"
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onClick={handleClick}
      style={{ transform: `translate3d(${dragPos.x}px, ${dragPos.y}px, 0)`, touchAction: "none" }}
      className="fixed right-4 bottom-[calc(4rem+env(safe-area-inset-bottom)+0.75rem)] z-40 flex cursor-grab touch-none items-center gap-2 rounded-full border border-amber-400/50 bg-amber-400/15 px-4 py-2 text-sm font-semibold text-amber-600 shadow-lg backdrop-blur-md transition-colors hover:bg-amber-400/25 active:cursor-grabbing dark:text-amber-300 md:right-6 md:bottom-6"
      aria-label={`Ưu đãi nạp nhanh +${percent}% còn ${countdown} ${unit}`}
    >
      <Zap className="h-4 w-4 fill-current" />
      <span>
        +{percent}% · <span className="tabular-nums">{countdown}</span>
        {unit ? ` ${unit}` : ""}
      </span>
    </button>
  );
}
